/**
 * ============================================================================
 * RevenueStats - MAIN STATS CARDS DISPLAY
 * ============================================================================
 *
 * ⚠️  CRITICAL WARNINGS FOR FUTURE CLAUDE SESSIONS:
 *
 * 1. PURE PRESENTATION COMPONENT
 *    - Receives ALL data via props
 *    - No calculations here (do them in parent with useMemo)
 *    - No data fetching
 *    - Just renders what it's given
 *
 * 2. CLICKABLE STATS
 *    - Some stats are clickable (Pending Payouts)
 *    - onViewHeld callback opens HeldBookingsModal
 *    - Not all stats need to be clickable
 *    - Keep interaction patterns consistent
 *
 * 3. RESPONSIVE GRID
 *    - 1 column on mobile
 *    - 2 columns on sm
 *    - 3 columns on lg
 *    - 5 columns on xl (perfect for 5 stats)
 *    - Never hardcode 5 columns at small sizes
 *
 * 4. ICON + COLOR SCHEME
 *    - Each stat has icon and background color
 *    - Colors match semantic meaning (green = good, red = refunds)
 *    - Icons from lucide-react
 *    - Keep visual hierarchy clear
 *
 * 5. FORMAT AMOUNTS HERE
 *    - Use formatCurrency from revenueFormatters
 *    - Don't expect formatted strings from parent
 *    - Parent passes raw numbers, we format
 *
 * 6. WORKS FOR SUPER ADMIN TOO
 *    - This component doesn't care if it's one venue or aggregated
 *    - Just receives numbers and displays them
 *    - Perfect for reusability
 *
 * ============================================================================
 */

import React from 'react';
import { DollarSign, Wallet, TrendingUp, RefreshCw, Clock } from 'lucide-react';
import { formatCurrency } from '../../utils/revenueFormatters';

interface RevenueStatsProps {
  totalRevenue: number;
  vendorEarnings: number;
  vendorSharePercentage: number;
  platformCommission: number;
  platformSharePercentage: number;
  totalRefunds: number;
  refundCount: number;
  heldBalance: number;
  heldBookingsCount: number;
  outstandingDebt?: number;
  netEarnings?: number;
  sourceFilter?: 'all' | 'spotts' | 'manual';
  onViewHeld?: () => void;
}

/**
 * Revenue statistics cards - main KPIs
 */
export function RevenueStats({
  totalRevenue,
  vendorEarnings,
  vendorSharePercentage,
  platformCommission,
  platformSharePercentage,
  totalRefunds,
  refundCount,
  heldBalance,
  heldBookingsCount,
  outstandingDebt = 0,
  netEarnings,
  sourceFilter = 'all',
  onViewHeld,
}: RevenueStatsProps) {
  // Calculate net earnings if not provided
  const actualNetEarnings = netEarnings ?? (vendorEarnings - outstandingDebt);
  const hasDebt = outstandingDebt > 0;

  // Build subtext for earnings based on source filter
  // "All" mode: percentage is misleading (manual=100%, spotts=X%), so don't show it
  // "Spotts" mode: show actual percentage split
  // "Manual" mode: always 100% (no commission)
  let earningsSubtext: string;
  if (hasDebt) {
    earningsSubtext = sourceFilter === 'spotts'
      ? `${vendorSharePercentage}% (Debt: -${formatCurrency(outstandingDebt)})`
      : `Debt: -${formatCurrency(outstandingDebt)}`;
  } else if (sourceFilter === 'spotts') {
    earningsSubtext = `${vendorSharePercentage}% of revenue`;
  } else if (sourceFilter === 'manual') {
    earningsSubtext = '100% (no commission)';
  } else {
    earningsSubtext = 'After platform commission';
  }

  // Define stats array for easy mapping
  const stats = [
    {
      label: 'Total Revenue',
      value: formatCurrency(totalRevenue),
      subtext: 'All bookings & memberships',
      icon: DollarSign,
      color: 'bg-green-50 text-green-600',
      clickable: false,
    },
    {
      label: hasDebt ? 'Net Earnings' : 'Your Earnings',
      value: formatCurrency(hasDebt ? actualNetEarnings : vendorEarnings),
      subtext: earningsSubtext,
      icon: Wallet,
      color: hasDebt ? 'bg-amber-50 text-amber-600' : 'bg-blue-50 text-blue-600',
      clickable: false,
    },
    {
      label: 'Platform Comm.',
      value: formatCurrency(platformCommission),
      subtext: `${platformSharePercentage}% fee`,
      icon: TrendingUp,
      color: 'bg-orange-50 text-orange-600',
      clickable: false,
    },
    {
      label: 'Total Refunds',
      value: formatCurrency(totalRefunds),
      subtext: `${refundCount} refund${refundCount !== 1 ? 's' : ''} processed`,
      icon: RefreshCw,
      color: 'bg-red-50 text-red-600',
      clickable: false,
    },
    {
      label: 'Pending Payouts',
      value: formatCurrency(heldBalance),
      subtext: `${heldBookingsCount} booking${heldBookingsCount !== 1 ? 's' : ''} held`,
      icon: Clock,
      color: 'bg-purple-50 text-purple-600',
      clickable: true,
      onClick: onViewHeld,
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
      {stats.map((stat, idx) => {
        const StatCard = stat.clickable && stat.onClick ? 'button' : 'div';
        const clickableProps = stat.clickable && stat.onClick
          ? {
              onClick: stat.onClick,
              className: 'bg-white rounded-xl p-3 border border-gray-200 text-left transition-all hover:border-purple-300 cursor-pointer',
            }
          : {
              className: 'bg-white rounded-xl p-3 border border-gray-200',
            };

        return (
          <StatCard key={idx} {...clickableProps}>
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-gray-600 text-xs mb-1 truncate">{stat.label}</p>
                <p className="text-lg sm:text-xl font-black text-gray-900 truncate">{stat.value}</p>
                <p className="text-xs text-gray-500 truncate">{stat.subtext}</p>
              </div>
              <div
                className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${stat.color} flex items-center justify-center flex-shrink-0 ml-2`}
              >
                <stat.icon className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
            </div>
          </StatCard>
        );
      })}
    </div>
  );
}
